/* global  L */

// Set mapbox API access token
if (typeof (L) !== 'undefined') {
    L.mapbox.accessToken = 'pk.eyJ1IjoiY2xhc3NpY2NvdHRhZ2VzIiwiYSI6ImNpcmJ3djZjNDAwNXNpOW1nbTF3bjIwcjcifQ.wUw-oQaU0UmbS3vsv_e8yQ'
}

export function streetLayer(provider = 'osm') {
    switch (provider) {
        case 'osm':
            return L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                tileSize: 256,
                minZoom: 1,
                maxZoom: 19,
                attribution: 'Map data © <a href="https://openstreetmap.org">OpenStreetMap</a> contributors',
                crossOrigin: true,
            })
        case 'mapbox':
            return L.mapbox.styleLayer('mapbox://styles/mapbox/streets-v10')
        default:
            return null
    }
}

export function satelliteLayer(provider = 'mapbox') {
    switch (provider) {
    case 'mapbox':
        return L.mapbox.styleLayer('mapbox://styles/mapbox/satellite-streets-v10')
    default:
        return null
    }
}

class Map {
    constructor(elementId, options = {}, customLayers = null) {
        this.container = document.getElementById(elementId)
        const opts = Object.assign({
            zoom: 9,
            maxZoom: 15,
            minZoom: 7,
        }, options)

        const layers = customLayers || {
            Streets: streetLayer(),
            Satellite: satelliteLayer(),
        }

        this.map = L.mapbox.map(elementId, null, opts)
        // assign the first layer as default
        layers[Object.keys(layers)[0]].addTo(this.map)

        // if there are more than one layers, show the control to
        // be able to switch between them
        if (Object.keys(layers).length > 1) {
            L.control.layers(layers, {}, { position: 'topleft' }).addTo(this.map)
        }

        // empty arrays for additional ui elements
        this.markers = []
        this.shapes = []
        this.cluster = null
    }

    addMarker(coords, options = {}, popupContent = null) {
        const defaults = {
            zindex: 1,
        }

        const marker = L.marker(coords, Object.assign({}, defaults, options)).addTo(this.map)
        this.markers.push(marker)

        if (popupContent !== null) {
            marker.bindPopup(popupContent)
        } else if (options.title) {
            marker.bindPopup(`${options.title}`)
        }

        return marker
    }

    fitMarkers(options = {}) {
        const boundsOpts = Object.assign({
            padding: [10, 10],
        }, options)

        if (this.markers.length > 0) {
            // create a group to hold all shapes
            const group = L.featureGroup(this.markers)
            // reference this group to retrieve the combined bounds
            this.map.fitBounds(group.getBounds(), boundsOpts)
        }

        return this
    }

    clusterMarkers() {
        if (this.cluster === null) {
            this.cluster = L.markerClusterGroup({
                showCoverageOnHover: false,
                maxClusterRadius: 30,
                spiderLegPolylineOptions: { weight: 3, color: '#206FA2', opacity: 1 },
                spiderfyDistanceMultiplier: 2,
            })
            this.map.addLayer(this.cluster)
        }

        this.markers.forEach((marker) => {
            marker.remove()
            this.cluster.addLayer(marker)
        })
    }


    clearMarkers() {
        if (this.cluster !== null) {
            this.cluster.clearLayers()
        }

        this.markers.forEach((marker) => {
            marker.remove()
        })
        this.markers = []

        return this
    }

    addShape(wkt, options = {}) {
        const shapeOpts = Object.assign({
            weight: 3,
            color: '#29abe3',
            opacity: 1,
            fillColor: '#29abe3',
            fillOpacity: 0.5,
        }, options)
        const coords = this.parseWKT(wkt)

        if (coords !== null) {
            const shape = L.polyline(coords, shapeOpts).addTo(this.map)
            this.shapes.push(shape)
        }
        return this
    }

    fitShapes(options = {}) {
        const boundsOpts = Object.assign({
            padding: [10, 10],
        }, options)

        if (this.shapes.length > 0) {
            // create a group to hold all shapes
            const group = L.featureGroup(this.shapes)
            // reference this group to retrieve the combined bounds
            this.map.fitBounds(group.getBounds(), boundsOpts)
        }
        return this
    }

    clearShapes() {
        this.shapes.forEach((shape) => {
            shape.remove()
        })
        this.shapes = []

        return this
    }

    /* eslint class-methods-use-this:0 */
    parseWKT(wkt = '') {
        if (wkt !== '') {
            const parameters = wkt.match(/([A-Z ]+)\([(]?([0-9-. ,]+)\)[)]?/)
            if (parameters) {
                const coords = []
                const lls = parameters[2].split(',')
                lls.forEach((ll) => {
                    const llarr = ll.trim().split(' ')
                    coords.push(L.latLng(...llarr.reverse()))
                })
                return coords
            }
        }
        return null
    }
}

export default Map
